import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import ColorPicker from 'rc-color-picker'
import { useDispatch } from 'react-redux'
import { toastr } from 'react-redux-toastr'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import GreyHeader from '../../../component/dump/menuComponents/GreyHeader'
import CustomButton from '../../../component/CustomButton';
import {
	constants, faviconsFolder, headerLogosFolder, headerLogosExtendedFolder, loadersFolder, faviconsList, headerLogosList, headerLogosExtendedList, loadersList,
} from '../../../component/helpers/constants'

import 'rc-color-picker/assets/index.css';
import { getPartnerWhitelabel, savePartnerWhitelabel } from "../../../action/CompaniesActions";

const PartnerWhitelabelSettings = (props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { t } = useTranslation('translation', { keyPrefix: 'partnersPage.partnerWhitelabelSettings' });
	const [initialSettings, setInitialSettings] = useState(JSON.parse(JSON.stringify(constants.defaultPartnerWhitelabelSettings)));
	const [settings, setSetings] = useState(JSON.parse(JSON.stringify(constants.defaultPartnerWhitelabelSettings)));
	const font = useRef(null);

	const themeColors = [
		{ title: t('primaryColor'), name: 'themecolors.primary' },
		{ title: t('secondaryColor'), name: 'themecolors.secondary' },
		{ title: t('tertiaryColor'), name: 'themecolors.tertiary' },
		{ title: t('alternate/ParkingColor'), name: 'themecolors.alternate' }
	];

	const isPartnerRoviTracker = location.state.partnerId === 3041 || location.state.partnerId === 1974,
		availableFontsArr = !isPartnerRoviTracker ? constants.availableFonts.filter(e => e !== 'Titillium') : constants.availableFonts,
		faviconsListArr = !isPartnerRoviTracker ? faviconsList.filter(e => e !== 'rovitracker_favicon.png') : faviconsList,
		headerLogosListArr = !isPartnerRoviTracker ? headerLogosList.filter(e => e !== 'rovitracker_logo.png') : headerLogosList,
		headerLogosExtendedListArr = !isPartnerRoviTracker ? headerLogosExtendedList.filter(e => e !== 'rovitracker_logo_ext.png') : headerLogosExtendedList;

	useEffect(() => {
		getPartnerWhitelabelSettings();
	}, []);

	const toggleLoader = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}, [dispatch]);

	const getPartnerWhitelabelSettings = () => {
		toggleLoader(true);
		getPartnerWhitelabel({ company_id: location.state.partnerId })
			.then((res) => {
				toggleLoader(false);
				const { settings } = res.data.response;
				if (!_.isEmpty(settings)) {
					setInitialSettings(JSON.parse(settings.settings));
					setSetings(JSON.parse(settings.settings));
				}
			})
			.catch((error) => {
				toggleLoader(false);
				console.log('getPartnerWhitelabelSettings /partner/{partner_id}/settings error: ', error.response, error)
				toastr.error(t('unableGetWhitelabelSettings'));
			})
	}

	const handleSaveClick = () => {
		if (_.isEqual(settings, initialSettings)) {
			toastr.warning('', t('whitelabelSettingsNoChanges'))
			return
		}

		const params = {
			partner_id: location.state.partnerId,
			settings: JSON.stringify(settings)
		};

		toggleLoader(true);
		savePartnerWhitelabel(params)
			.then((res) => {
				toggleLoader(false);

				const updatedSettings = res.data.response.settings;
				if (!_.isEmpty(updatedSettings)) {
					setInitialSettings(JSON.parse(updatedSettings.settings));
					setSetings(JSON.parse(updatedSettings.settings));
				}

				toastr.success(t('changesSaved'));
			})
			.catch((error) => {
				toggleLoader(false);
				console.log('!PartnerWhitelabelSettings /partner/{partner_id}/settings error: ', error.response, error)
				toastr.error(t('unableUpdateWhitelabelSettings'));
			});
	};

	const handleResetClick = () => {
		if (_.isEqual(settings, initialSettings)) {
			toastr.warning('', t('whitelabelSettingsNoChanges'))
			return
		}
		setSetings(JSON.parse(JSON.stringify(initialSettings)));
	}

	const handleDefaultClick = () => {
		if (_.isEqual(settings, constants.defaultPartnerWhitelabelSettings)) {
			toastr.warning('', t('whitelabelSettingsDefault'))
			return
		}

		setSetings(JSON.parse(JSON.stringify(constants.defaultPartnerWhitelabelSettings)));
	}

	const handleSettingChange = (settingValue, settingPath) => {
		const newSettings = _.set({ ...settings }, settingPath, settingValue);
		setSetings(newSettings);
	}

	const addFont = () => {
		let valueFont = font.current.value;
		const fontArr = settings.font.length ? settings.font.split(',') : []

		if (!fontArr.includes(valueFont)) {
			fontArr.push(valueFont)
			handleSettingChange(fontArr.join(','), 'font')
		}
	}

	return (
		<div className="partner-whitelabel">
			<GreyHeader title={t('titlePartnerSettings')} subtitle={location.state.partnerName} />

			<div className="page-subheader">
				<div className="subheader-section search-flex" />
				<div className="subheader-section">
					<CustomButton variant="secondary" onClick={handleSaveClick}>{t('btnSave')}</CustomButton>
					<CustomButton variant="secondary-outline" onClick={() => navigate(-1)}>{t('btnBack')}</CustomButton>
					<CustomButton variant="delete" onClick={handleResetClick}>{t('btnReset')}</CustomButton>
					<CustomButton variant="delete" onClick={handleDefaultClick} title="Set Rosco default settings">{t('btnDefault')}</CustomButton>
				</div>
			</div>

			<main className="main-content-block">
				<section>
					<h3 className="category-title">{t('titleColorScheme')}</h3>
					<div className="four-column-grid with-margin">
						{themeColors.map(({ title, name }) => {
							return (
								<div key={name}>
									<div>
										<label className="control-label">{title}:</label>
									</div>
									<ColorPicker
										color={_.get(settings, name)}
										alpha={100}
										enableAlpha={false}
										onChange={color => handleSettingChange(color.color, name)}
									>
										<span className="rc-color-picker-trigger custom-style" />
									</ColorPicker>
									<div>{_.get(settings, name)}</div>
								</div>
							)
						})
						}
					</div>
				</section>

				<section>
					<h3 className="category-title">{t('titleFontFamily')}</h3>
					<div className="two-column-grid with-margin">
						<div>
							<label className="control-label">{t('labelAvailableFont')}</label>
							<div className="upload-button-wrapper">
								<select
									ref={font}
									className="rosco-select block"
									style={{ marginRight: '20px' }}
								>
									{availableFontsArr.map(name => (
										<option key={name} value={name} className="menu-item">
											{name}
										</option>
									))}
								</select>
								<CustomButton
									variant="primary-outline"
									onClick={addFont}
								>
									{t('btnAdd')}
								</CustomButton>
							</div>
						</div>
						<div>
							<label className="control-label">{t('labelSelectedFont')}</label>
							<div className="upload-button-wrapper">
								<input
									type="text"
									className="vehicle-search"
									readOnly
									value={settings.font}
									style={{ marginRight: '20px' }}
								/>
								<CustomButton
									variant="primary-outline"
									onClick={() => handleSettingChange('', 'font')}
								>
									{t('btnClear')}
								</CustomButton>
							</div>
						</div>
					</div>
				</section>

				<section>
					<h3 className="category-title">{t('titleTabAttributes')}</h3>
					<div className="two-column-grid with-margin">
						<div>
							<label className="control-label">{t('labelTabTitle')}</label>
							<input
								type="text"
								className="vehicle-search"
								onChange={event => handleSettingChange(event.target.value, 'title')}
								value={settings.title}
							/>
						</div>
						<div>
							<label className="control-label">{t('labelTabFavicon')}</label>
							<div className="upload-button-wrapper">
								<select
									className="rosco-select block"
									style={{ marginRight: '20px', marginTop: '1px' }}
									value={settings.favicon}
									onChange={event => handleSettingChange(event.target.value, 'favicon')}
								>
									<option />
									{faviconsListArr.map(name => (
										<option key={name} value={name} className="menu-item">
											{name}
										</option>
									))}
								</select>
								{settings.favicon && (
									<img
										className="image-favicon"
										src={faviconsFolder + settings.favicon}
										alt={t('altTabFavicon')}
									/>
								)}
							</div>
						</div>
					</div>
				</section>

				<section>
					<h3 className="category-title">{t('titleCustomLogo')}</h3>
					<div className="three-column-grid with-margin">
						<div>
							<label className="control-label">{t('labelHeaderLogo')}</label>
							<div className="upload-button-wrapper">
								<select
									className="rosco-select block"
									style={{ marginRight: '20px' }}
									value={settings.header_logo}
									onChange={event => handleSettingChange(event.target.value, 'header_logo')}
								>
									<option />
									{headerLogosListArr.map(name => (
										<option key={name} value={name} className="menu-item">
											{name}
										</option>
									))}
								</select>
								{settings.header_logo && (
									<img
										className="image-favicon"
										src={headerLogosFolder + settings.header_logo}
										alt={t('altHeaderLogo')}
									/>
								)}
							</div>
						</div>

						<div>
							<label className="control-label">{t('labelHeaderExtendedLogo')}</label>
							<div className="upload-button-wrapper">
								<select
									className="rosco-select block"
									style={{ marginRight: '20px' }}
									value={settings.header_logo_ext}
									onChange={event => handleSettingChange(event.target.value, 'header_logo_ext')}
								>
									<option />
									{headerLogosExtendedListArr.map(name => (
										<option key={name} value={name} className="menu-item">
											{name}
										</option>
									))}
								</select>
								{settings.header_logo_ext && (
									<img
										className="image-favicon"
										src={headerLogosExtendedFolder + settings.header_logo_ext}
										alt={t('altHeaderExtendedLogo')}
									/>
								)}
							</div>
						</div>

						<div>
							<label className="control-label">{t('labelLoader')}</label>
							<div className="upload-button-wrapper">
								<select
									className="rosco-select block"
									style={{ marginRight: '20px' }}
									value={settings.loader}
									onChange={event => handleSettingChange(event.target.value, 'loader')}
								>
									<option />
									{loadersList.map(name => (
										<option key={name} value={name} className="menu-item">
											{name}
										</option>
									))}
								</select>
								{settings.loader && (
									<img
										className="image-favicon"
										src={loadersFolder + settings.loader}
										alt={t('altHeaderExtendedLogo')}
									/>
								)}
							</div>
						</div>
					</div>
				</section>

				<section>
					<h3 className="category-title">{t('titleFooter')}</h3>
					<div className="two-column-grid with-margin">
						<div className="two-columns">
							<textarea
								value={_.get(settings, 'footer') || ''}
								onChange={event => handleSettingChange(event.target.value, 'footer')}
								style={{ width: '100%', height: '80px', resize: 'vertical' }}
							/>
							<div dangerouslySetInnerHTML={{ __html: _.get(settings, 'footer') || '' }} />
						</div>
					</div>
				</section>
			</main>
		</div>
	)
}

export default PartnerWhitelabelSettings;
